import React from 'react';
import { format, parseISO } from 'date-fns';
import Modal from 'src/shared/components/modal/Modal';
import { Badge } from 'src/shared/components/ui/badge';
import type { ApiTokenSummary } from '../../state/api/engineApiTokensEngineSlice';

type TokenDetailModalProps = {
    token: ApiTokenSummary;
    onClose: () => void;
};

const formatDateTime = (iso: string | null): string => {
    if (!iso) return 'Never';
    return format(parseISO(iso), "d MMM yyyy 'at' HH:mm");
};

type FieldProps = { label: string; children: React.ReactNode };
const Field = ({ label, children }: FieldProps) => (
    <div className='flex flex-col gap-1'>
        <span className='text-xs uppercase tracking-wide text-grey-60'>{label}</span>
        <span className='text-sm text-primary break-all'>{children}</span>
    </div>
);

const TokenDetailModal = ({ token, onClose }: TokenDetailModalProps) => {
    const isRevoked = token.revoked_at !== null;

    return (
        <Modal onClose={onClose} dataTestId='token-detail-modal'>
            <div className='flex flex-col gap-6 p-2 min-w-[28rem] max-w-2xl'>
                <header className='flex flex-col gap-1'>
                    <h2 className='text-xl font-semibold text-primary'>{token.name}</h2>
                    <span className='text-sm text-grey-70'>Engine API token details</span>
                </header>

                <div className='grid grid-cols-2 gap-x-6 gap-y-4'>
                    <Field label='ID'>
                        <code className='text-xs'>{token.id}</code>
                    </Field>
                    <Field label='Prefix'>
                        <code className='text-xs bg-grey-10 px-2 py-1 rounded'>{token.token_prefix}</code>
                    </Field>
                    <Field label='Created by'>{token.created_by}</Field>
                    <Field label='Created at'>{formatDateTime(token.created_at)}</Field>
                    <Field label='Last used'>{formatDateTime(token.last_used_at)}</Field>
                    <Field label='Status'>
                        {isRevoked ? <Badge variant='error'>Revoked</Badge> : <Badge variant='success'>Active</Badge>}
                    </Field>
                </div>

                <Field label='Scopes'>
                    <div className='flex flex-wrap gap-1 mt-1'>
                        {token.allowed_scopes.map(scope => (
                            <span key={scope} className='text-xs bg-grey-10 px-2 py-0.5 rounded'>
                                {scope}
                            </span>
                        ))}
                    </div>
                </Field>

                {isRevoked && (
                    <div className='grid grid-cols-2 gap-x-6 gap-y-4 pt-2 border-t border-grey-10'>
                        <Field label='Revoked at'>{formatDateTime(token.revoked_at)}</Field>
                        <Field label='Revoked by'>{token.revoked_by ?? '—'}</Field>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default TokenDetailModal;
